export enum NotificationStatusEnum {
  READ = 'READ',
  UNREAD = 'UNREAD',
}

export interface NotificationAPIResponse {
  mun_id: number
  mun_fk_usr_id: number
  mun_fk_mp_id: number
  mun_fk_mt_id: number
  mun_status: string
  mun_subject: string
  mun_body: string
  mun_date_created: string
}
export interface NotificationFBResponse {
  munId: number
  munUserId: number
  munFkPltIid: number
  munFkTrdId: number
  munStatus: string
  munSubject: string
  munBody: string
  dateCreated: string
}

export interface CanxNotificationBody {
  prod_name: string
  prod_qty: string
  prod_qty_type: string
  prod_price: string
  prod_price_cur: string
  breadcrumb: string
  entity: string
  entity_field: string
  entity_field_value: string
  prod_potency: string
  sample_id?: string
  dateUpdated: string
}

export interface CanxNotification {
  id: number
  userId: number
  placementId: number
  tradeId: number
  status: string
  subject: string
  body?: CanxNotificationBody
  date: string
  breadcrumb?: BreadcrumbInfo
}
export interface BreadcrumbInfo {
  partA: string
  path: string
  type: string
  action: string
}

export const toNotification = (apiResponse: NotificationAPIResponse, logger: CanxLogger): CanxNotification => {
  let body
  try {
    body = JSON.parse(apiResponse.mun_body)
  }
  catch (e) {
    logger.error('Error parsing JSON', apiResponse, e as Error)
  }
  const breadcrumb = <BreadcrumbInfo>{}
  if (body.breadcrumb) {
    const [partA, path, type, action] = body.breadcrumb.toLowerCase().split('.')

    breadcrumb.partA = partA
    breadcrumb.path = path
    breadcrumb.type = type
    breadcrumb.action = action
  }
  return {
    id: apiResponse.mun_id,
    userId: apiResponse.mun_fk_usr_id,
    placementId: apiResponse.mun_fk_mp_id,
    tradeId: apiResponse.mun_fk_mt_id,
    status: apiResponse.mun_status,
    subject: apiResponse.mun_subject,
    date: apiResponse.mun_date_created,
    body,
    breadcrumb,
  }
}

export const fbToNotification = (apiResponse: NotificationFBResponse, logger: CanxLogger): CanxNotification => {
  let body
  try {
    body = JSON.parse(apiResponse.munBody)
  }
  catch (e) {
    logger.error('Error parsing JSON', apiResponse, e as Error)
  }
  const breadcrumb = <BreadcrumbInfo>{}
  if (body.breadcrumb) {
    const [partA, path, type, action] = body.breadcrumb.split('.')

    breadcrumb.partA = partA
    breadcrumb.path = path
    breadcrumb.type = type
    breadcrumb.action = action
  }
  return {
    id: apiResponse.munId,
    userId: apiResponse.munUserId,
    placementId: apiResponse.munFkPltIid,
    tradeId: apiResponse.munFkTrdId,
    status: apiResponse.munStatus,
    subject: apiResponse.munSubject,
    date: apiResponse.dateCreated,
    body,
    breadcrumb,
  }
}

type NotificationToString = (notif: CanxNotification) => string

const generatePartialURL = (
  baseURL: string,
  params: Record<string, string>,
  extraQueryStringGetter: NotificationToString,
) => {
  let url = baseURL + '?'
  Object.entries(params).forEach(([key, value]) => url += `${key}=${value}&`)
  return (notif: CanxNotification) => {
    return `${url}${extraQueryStringGetter(notif)}`
  }
}

// if action is completed transaction, then if is sample get sample_id else get trade_id
// if type is sell or buy, then get entity_field_value
// if type is sample, then get sample_id
const interestNotificationToQueryString = (notif: CanxNotification) => `id=${notif.placementId}`
const notificationToEntityFieldValue = (notif: CanxNotification) => `id=${notif.body?.entity_field_value || ''}`
const sampleNotificationToQueryString = (notif: CanxNotification) => `id=${notif.body?.sample_id || ''}`
const completedNotificationToQueryString = (notif: CanxNotification) =>
  `idType=${notif.body?.sample_id ? 'sample' : 'trade'}&id=${notif.body?.sample_id ? notif.body?.sample_id : notif.body?.entity_field_value}`

const breadcrumbURLMap = new Map<string, NotificationToString>([
  ['client.my-interests.sell-offers.table', generatePartialURL('/published-interests', { pbType: 'SELL' }, interestNotificationToQueryString)],
  ['client.my-interests.sell.table', generatePartialURL('/published-interests', { pbType: 'SELL' }, notificationToEntityFieldValue)],
  ['client.my-interests.buy.table', generatePartialURL('/published-interests', { pbtype: 'BUY' }, notificationToEntityFieldValue)],
  ['client.interests.buy.criteria', generatePartialURL('/published-interests', { pbtype: 'BUY' }, interestNotificationToQueryString)],
  ['client.interests.sell.criteria', generatePartialURL('/published-interests', { pbtype: 'SELL' }, interestNotificationToQueryString)],

  ['client.negotiations.sell.pending', generatePartialURL('/negotiations', { action: NegotiationStatusEnum.PENDING, type: TransactionTypeEnum.SELL }, notificationToEntityFieldValue)],
  ['client.negotiations.buy.pending', generatePartialURL('/negotiations', { action: NegotiationStatusEnum.PENDING, type: TransactionTypeEnum.BUY }, notificationToEntityFieldValue)],
  ['client.negotiations.sell.actionable', generatePartialURL('/negotiations', { action: NegotiationStatusEnum.ACTIONABLE, type: TransactionTypeEnum.SELL }, notificationToEntityFieldValue)],
  ['client.negotiations.buy.actionable', generatePartialURL('/negotiations', { action: NegotiationStatusEnum.ACTIONABLE, type: TransactionTypeEnum.BUY }, notificationToEntityFieldValue)],
  ['client.negotiations.sample.pending', generatePartialURL('/negotiations', { action: NegotiationStatusEnum.PENDING, type: TransactionTypeEnum.SAMPLE }, sampleNotificationToQueryString)],
  ['client.negotiations.sample.actionable', generatePartialURL('/negotiations', { action: NegotiationStatusEnum.ACTIONABLE, type: TransactionTypeEnum.SAMPLE }, sampleNotificationToQueryString)],
  ['client.completed-transactions.sell.table', generatePartialURL('/negotiations', { action: NegotiationStatusEnum.COMPLETED, type: TransactionTypeEnum.SELL }, completedNotificationToQueryString)],
  ['client.completed-transactions.buy.table', generatePartialURL('/negotiations', { action: NegotiationStatusEnum.COMPLETED, type: TransactionTypeEnum.BUY }, completedNotificationToQueryString)],
])

export const extractURLFromNotification = (notif: CanxNotification): string => {
  const { partA, path, type, action } = notif.breadcrumb ?? {}
  const key = `${partA}.${path}.${type?.toLowerCase()}.${action?.toLowerCase()}`
  const cmd = breadcrumbURLMap.get(key)
  if (!cmd) throw new Error(`Unknown command for ${key}`)
  return cmd(notif)
}
